import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MdEmail, MdPerson, MdLock, MdPhone, MdCheckCircle, MdErrorOutline, MdVisibility, MdVisibilityOff } from "react-icons/md";
import { FiCheck } from "react-icons/fi";
import baseURL from "../../config/api";

const FALLBACK_IMAGE = "/images/logo-no-background.png";

type RegisterInvitationResponse = {
  message?: string;
  Message?: string;
};

const PASSWORD_REQUIREMENTS = [
  {
    label: "At least 8 characters",
    test: (value: string) => value.length >= 8,
  },
  {
    label: "One uppercase letter (A-Z)",
    test: (value: string) => /[A-Z]/.test(value),
  },
  {
    label: "One lowercase letter (a-z)",
    test: (value: string) => /[a-z]/.test(value),
  },
  {
    label: "One number (0-9)",
    test: (value: string) => /\d/.test(value),
  },
  {
    label: "One special character (!@#$%^&*())",
    test: (value: string) => /[!@#$%^&*(),.?":{}|<>]/.test(value),
  }
];

function getApiMessage(
  data: unknown,
  fallback: string
): string {
  if (typeof data === "string" && data.trim()) {
    return data;
  }

  if (data && typeof data === "object") {
    const body = data as {
      message?: string;
      errors?: Record<string, string[]>;
    };

    if (body.message) {
      return body.message || fallback;
    }

    if (body.errors) {
      const validationMessage = Object.values(body.errors)
        .flat()
        .find(Boolean);

      if (validationMessage) {
        return validationMessage;
      }
    }
  }

  return fallback;
}

const AcceptedInvitation: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = searchParams.get("token") ?? "";

  const [isValidating, setIsValidating] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInvitationValid, setIsInvitationValid] = useState(false);

  const [invitationEmail, setInvitationEmail] = useState("");
  const [groupName, setGroupName] = useState("");

  const [imageSource, setImageSource] = useState(FALLBACK_IMAGE);
  const [hasImageFailed, setHasImageFailed] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);

  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const passwordRequirements = useMemo(() =>
    PASSWORD_REQUIREMENTS.map((requirement) => ({
      label: requirement.label,
      valid: requirement.test(password)
    })), [password]);

  const isPasswordValid = passwordRequirements.every((requirement) => requirement.valid);

  const passwordStrength = passwordRequirements.filter((requirement) => requirement.valid).length;

  const passwordStrengthLabel =
    passwordStrength <= 2
      ? "Weak"
      : passwordStrength <= 4
        ? "Medium"
        : "Strong";

  const passwordStrengthColor =
    passwordStrength <= 2
      ? "bg-red-500"
      : passwordStrength <= 4
        ? "bg-amber-500"
        : "bg-emerald-500";

  const passwordsMatch =
    confirmPassword.length > 0 && password === confirmPassword;

  useEffect(() => {
    let cancelled = false;

    const validateInvitation = async () => {
      if (!token) {
        setIsInvitationValid(false);
        setErrorMessage("The invitation link is missing or invalid.");
        setIsValidating(false);
        return;
      }

      try {
        const response = await fetch(`${baseURL}memberInvitation/ValidateInvitation?token=${encodeURIComponent(token)}`);

        const data: any = await response
          .json()
          .catch(() => ({}));

        if (!response.ok) {
          throw new Error(
            getApiMessage(data, "Unable to validate this invitation.")
          );
        }

        if (cancelled) return;

        const valid = data.isValid ?? false;

        if (!valid) {
          setIsInvitationValid(false);
          setErrorMessage(
            data.message ??
            "This invitation is invalid or has already been used."
          );
          return;
        }

        const resolvedGroupImage = data.groupImage ?? "";

        setIsInvitationValid(true);
        setInvitationEmail(data.email ?? data.Email ?? "");
        setGroupName(data.groupName ?? data.GroupName ?? "");

        setImageSource(
          resolvedGroupImage
            ? `${baseURL}commonFile/Get?name=${encodeURIComponent(resolvedGroupImage)}`
            : FALLBACK_IMAGE
        );
        setHasImageFailed(false);
      } catch (error: unknown) {
        if (cancelled) return;

        setIsInvitationValid(false);
        setErrorMessage(
          error instanceof Error
            ? error.message
            : "Unable to validate your invitation."
        );
      } finally {
        if (!cancelled) {
          setIsValidating(false);
        }
      }
    };

    void validateInvitation();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
    setPasswordTouched(true);
    setErrorMessage("");
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage("");
    setPasswordTouched(true);

    if (!firstName.trim()) {
      setErrorMessage("Please enter your first name.");
      return;
    }

    if (!lastName.trim()) {
      setErrorMessage("Please enter your last name.");
      return;
    }

    if (!token || !isInvitationValid) {
      setErrorMessage("Your invitation is not valid.");
      return;
    }

    if (!isPasswordValid) {
      setErrorMessage(
        "Please meet all password requirements before continuing."
      );
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage("Password and confirm password do not match.");
      return;
    }

    try {
      setIsSubmitting(true);

      const response = await fetch(
        `${baseURL}memberInvitation/RegisterFamilyMemberFromInvitation`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            token,
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            phoneNumber: phoneNumber.trim(),
            password,
            confirmPassword
          }),
        }
      );

      const data: RegisterInvitationResponse = await response
        .json()
        .catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          getApiMessage(
            data,
            "Unable to complete your registration."
          )
        );
      }

      setSuccessMessage(
        data.message ??
        "Your registration has been completed successfully."
      );
      setIsInvitationValid(false);
    } catch (error: unknown) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Something went wrong. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isValidating) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="text-center">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-secondary border-r-transparent" />
          <p className="mt-4 text-sm text-gray-600">
            Validating your invitation...
          </p>
        </div>
      </div>
    );
  }

  if (successMessage) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md rounded-xl bg-white p-8 text-center shadow-lg">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-green-50">
            <MdCheckCircle className="text-green-600" size={48} />
          </div>

          <h1 className="mt-5 text-2xl font-bold text-gray-800">
            Registration Completed!
          </h1>

          <p className="mt-3 text-sm leading-6 text-gray-600">
            {successMessage}
          </p>

          <p className="mt-2 text-sm text-gray-500">
            Your account has been successfully created.
          </p>

          <button
            type="button"
            onClick={() => navigate("/sign-in")}
            className="mt-7 w-full rounded-md bg-secondary px-5 py-3 text-sm font-semibold text-white hover:bg-custom-blue"
          >
            Continue to Login
          </button>
        </div>
      </div>
    );
  }

  if (!isInvitationValid) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
        <div className="w-full max-w-md rounded-xl bg-white p-8 text-center shadow-lg">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-red-50">
            <MdErrorOutline className="text-red-500" size={48} />
          </div>

          <h1 className="mt-5 text-xl font-bold text-gray-800">
            Invalid Invitation
          </h1>

          <p className="mt-3 text-sm leading-6 text-gray-600">
            {errorMessage || "This invitation link is invalid or has already been accepted."}
          </p>

          <p className="mt-4 text-xs text-gray-500">
            Please contact your church administrator if you need a new invitation.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-4">
      <div className="w-full max-w-lg overflow-hidden rounded-xl border border-gray-100 bg-white shadow-lg">
        <div className="bg-secondary px-6 py-7 text-center text-white">
          {
            groupName && (
              <div className="mx-auto mb-4 flex h-20 w-20 items-center justify-center overflow-hidden rounded-full border-2 border-white/70 bg-white">
                {
                  !hasImageFailed
                    ? (
                      <img
                        src={imageSource}
                        alt={`${groupName} church`}
                        className="h-full w-full object-cover"
                        onError={() => {
                          if (imageSource !== FALLBACK_IMAGE) {
                            setImageSource(FALLBACK_IMAGE);
                          } else {
                            setHasImageFailed(true);
                          }
                        }}
                      />
                    ) : (
                      <span className="text-2xl font-bold text-secondary">
                        {groupName.charAt(0).toUpperCase()}
                      </span>
                    )
                }
              </div>
            )
          }

          <h1 className="text-2xl font-bold">
            Accept Your Invitation
          </h1>

          <p className="mt-2 text-sm text-white/90">
            {groupName
              ? `Join ${groupName} by completing your registration.`
              : "Complete your registration to join your church directory."}
          </p>
        </div>

        <div className="p-6 sm:p-8">
          <div className="mb-6">
            <label
              htmlFor="invitationEmail"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Invited Email Address
            </label>

            <div className="flex items-center rounded-md border border-gray-200 bg-gray-50 px-3">
              <MdEmail className="shrink-0 text-gray-400" size={20} />
              <input
                id="invitationEmail"
                type="email"
                value={invitationEmail}
                readOnly
                className="w-full bg-transparent px-3 py-3 text-sm text-gray-600 outline-none"
              />
            </div>

            <p className="mt-1 text-xs text-gray-400">
              This email address is associated with your invitation.
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label
                  htmlFor="firstName"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  First Name <span className="text-red-500">*</span>
                </label>

                <div className="flex items-center rounded-md border border-gray-300 px-3 focus-within:border-secondary">
                  <MdPerson className="shrink-0 text-gray-400" size={20} />
                  <input
                    id="firstName"
                    type="text"
                    value={firstName}
                    onChange={(event) => setFirstName(event.target.value)}
                    placeholder="First name"
                    autoComplete="given-name"
                    className="w-full px-3 py-3 text-sm outline-none"
                    required
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="lastName"
                  className="mb-2 block text-sm font-medium text-gray-700"
                >
                  Last Name <span className="text-red-500">*</span>
                </label>

                <div className="flex items-center rounded-md border border-gray-300 px-3 focus-within:border-secondary">
                  <MdPerson className="shrink-0 text-gray-400" size={20} />
                  <input
                    id="lastName"
                    type="text"
                    value={lastName}
                    onChange={(event) => setLastName(event.target.value)}
                    placeholder="Last name"
                    autoComplete="family-name"
                    className="w-full px-3 py-3 text-sm outline-none"
                    required
                  />
                </div>
              </div>
            </div>

            <div>
              <label
                htmlFor="phoneNumber"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Phone Number
              </label>

              <div className="flex items-center rounded-md border border-gray-300 px-3 focus-within:border-secondary">
                <MdPhone className="shrink-0 text-gray-400" size={20} />
                <input
                  id="phoneNumber"
                  type="tel"
                  value={phoneNumber}
                  onChange={(event) => setPhoneNumber(event.target.value)}
                  placeholder="Enter phone number"
                  autoComplete="tel"
                  className="w-full px-3 py-3 text-sm outline-none"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor="password"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Password <span className="text-red-500">*</span>
              </label>

              <div className="relative flex items-center rounded-md border border-gray-300 px-3 focus-within:border-secondary">
                <MdLock className="shrink-0 text-gray-400" size={20} />
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder="Create a password"
                  autoComplete="new-password"
                  className="w-full px-3 py-3 pr-2 text-sm outline-none"
                  required
                />

                <button
                  type="button"
                  onClick={() => setShowPassword((previous) => !previous)}
                  className="shrink-0 text-gray-400 hover:text-gray-700"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? (
                    <MdVisibilityOff size={20} />
                  ) : (
                    <MdVisibility size={20} />
                  )}
                </button>
              </div>

              {password.length > 0 && (
                <div className="mt-3">
                  <div className="mb-2 flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      Password strength
                    </span>
                    <span
                      className={`text-xs font-semibold ${passwordStrength <= 2
                        ? "text-red-500"
                        : passwordStrength <= 4
                          ? "text-amber-600"
                          : "text-green-600"
                        }`}
                    >
                      {passwordStrengthLabel}
                    </span>
                  </div>

                  <div
                    className="flex gap-1"
                    aria-label={`Password strength: ${passwordStrength} of 5 requirements met`}
                  >
                    {passwordRequirements.map((requirement, index) => (
                      <div
                        key={requirement.label}
                        className={`h-1.5 flex-1 rounded-full ${index < passwordStrength
                          ? passwordStrengthColor
                          : "bg-gray-200"
                          }`}
                      />
                    ))}
                  </div>

                  <ul className="mt-3 space-y-2">
                    {passwordRequirements.map((requirement) => (
                      <li
                        key={requirement.label}
                        className={`flex items-center gap-2 text-xs ${requirement.valid
                          ? "text-green-700"
                          : "text-gray-500"
                          }`}
                      >
                        <FiCheck
                          size={14}
                          className={
                            requirement.valid
                              ? "text-green-600"
                              : "text-gray-300"
                          }
                        />
                        {requirement.label}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {passwordTouched && password.length > 0 && !isPasswordValid && (
                <p className="mt-2 text-xs text-red-500">
                  Your password does not meet all the required criteria above.
                </p>
              )}
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="mb-2 block text-sm font-medium text-gray-700"
              >
                Confirm Password <span className="text-red-500">*</span>
              </label>

              <div className="relative flex items-center rounded-md border border-gray-300 px-3 focus-within:border-secondary">
                <MdLock className="shrink-0 text-gray-400" size={20} />
                <input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(event) => {
                    setConfirmPassword(event.target.value);
                    setErrorMessage("");
                  }}
                  placeholder="Confirm your password"
                  autoComplete="new-password"
                  className="w-full px-3 py-3 pr-2 text-sm outline-none"
                  required
                />

                <button
                  type="button"
                  onClick={() =>
                    setShowConfirmPassword((previous) => !previous)
                  }
                  className="shrink-0 text-gray-400 hover:text-gray-700"
                  aria-label={
                    showConfirmPassword
                      ? "Hide confirm password"
                      : "Show confirm password"
                  }
                >
                  {showConfirmPassword ? (
                    <MdVisibilityOff size={20} />
                  ) : (
                    <MdVisibility size={20} />
                  )}
                </button>
              </div>

              {confirmPassword.length > 0 && (
                <p
                  className={`mt-2 flex items-center gap-1 text-xs ${passwordsMatch ? "text-green-600" : "text-red-500"
                    }`}
                >
                  {passwordsMatch ? (
                    <>
                      <FiCheck size={14} />
                      Passwords match
                    </>
                  ) : (
                    "Passwords do not match"
                  )}
                </p>
              )}
            </div>

            {errorMessage && (
              <div
                role="alert"
                className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700"
              >
                <MdErrorOutline size={19} className="mt-0.5 shrink-0" />
                <span>{errorMessage}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={isSubmitting}
              className="flex w-full items-center justify-center gap-2 rounded-md bg-secondary px-5 py-3 text-sm font-semibold text-white hover:bg-custom-blue disabled:cursor-not-allowed disabled:opacity-60"
            >
              {isSubmitting ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-r-transparent" />
                  Completing Registration...
                </>
              ) : (
                <>
                  <MdCheckCircle size={19} />
                  Accept Invitation
                </>
              )}
            </button>

            <p className="text-center text-xs leading-5 text-gray-400">
              By completing registration, you will join the church directory
              associated with this invitation.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AcceptedInvitation;
